<template>
  <div
    class="tech-city"
    :class="[{'show-modal': techCityModalIsShown}]"
    ref="techCity">
    <div v-if="newsViewEnabled" class="tech-city-top-right-button news-view-button" aria-hidden @click="toggleNewsBitesClick" v-gtm-tracker="{ec: 'Tech City', ea: 'Tech City/News Bites/Click', el: $route.path}">
      <div v-show="showNewsBiteIndicator" class="indicator"></div>
      <span>News Bites</span><svg-news-view></svg-news-view>
    </div>
    <div class="tech-city-top-right-button email-collect-button" aria-hidden :class="[{'hidden': isShowTopRightButton || !isMapView, 'news-view-invisible': !newsViewEnabled}]" @click="showModal(false, {status: subscriptionStatusKeyUpdates})">
      <span>Get updates! </span><svg-email-collect></svg-email-collect>
    </div>
    <tech-city-welcome v-if="!loading && showWelcomeMessage && !isShowSubscription" ref="welcomeMessage" @isDone="welcomeMessageDone"></tech-city-welcome>
    <tech-city-subscribe v-if="isShowSubscription" :isUpdate="isUpdateMessage" ref="subscription" @popupChange="subscriptionPopupHandler"></tech-city-subscribe>
    <tech-city-modal
      v-if="mounted || pageType === 'tech-city-node'"
      :showModal="techCityModalIsShown || pageType === 'tech-city-node'"
      :nid="currentBuildingNid"
      :item="currentCityItem"
      @closeModal="closeModal"
      :isClosingModal="isClosingModal"
      :shareItem="{url: shareUrl, title: seoTitle}"
      ></tech-city-modal>
    <div class="tech-city__page-share page-share"
      v-show="!techCityModalIsShown && isShowPageShareButton"
      @click="shareTechCity()"
      v-gtm-tracker="{ec: 'Inbound Clicks', ea: 'China Tech City Map View/Share/Click', el: desktopOrMobile()}"
      >
      <toggle-share-buttons
        class="toggle-share-wrapper"
        v-if="mounted && shareUrl"
        :shareUrl="shareUrl"
        mode="Tech City"
        :title="seoTitle"
        @shareCountFetched="handleShareButton"
        @shareHover="shareHover"
        :showCount="true"
        :showNumberAnyway="true"
        :bypassUserInteraction="true"
        iconDesktop="down"
        ref="shareButtons"
        >
        <template v-slot:extra>
          <div class="share-panda" ref="sharePanda" :class="{animate: pandaAnimate, leave: pandaLeave, 'no-delay': pandaLeaveNow}">
            <div class="panda">
              <div class="panda-icon"></div>
              <div class="chat-bubbles" :class="{'slide-in': pandaChatBubble}">Share us if you like us!</div>
            </div>
          </div>
        </template>
      </toggle-share-buttons>

    </div>
    <pixijs-widget
      aria-hidden
      v-if="mounted && !isEmpty(property) && (allowRenderMap || initMap)"
      :freeze="freezeMap"
      :blur="blurMap"
      class="tech-city-map"
      ref="pixijsWidget"
      :artboardData="artboardData"
      :normalData="normalData"
      :extraData="extraData"
      :animationSpriteConfig="animationSpriteConfig"
      :imageSpriteSheetConfig="imageSpriteSheetConfig"
      :interactiveSpriteConfig="interactiveSpriteConfig"
      resourceDir="/static/tech-city"
      resourceType="png"
      @spriteMouseDown="spriteMouseDown"
      @spriteMouseClick="spriteMouseClick"
      @snapEnd="snapEnd"
      @snapZoomEnd="snapZoomEnd"
      @pixijsIsReady="pixijsIsReady"
      @startRenderPixi="startRenderPixi"
      @pixiLoaderOnStart="onPixiLoaderOnStart"
      @pixiLoaderOnProgress="onPixiLoaderOnProgress"
      @pixiLoaderOnComplete="onPixiLoaderOnComplete"
      ></pixijs-widget>
    <div class="loader-overlay" aria-hidden :class="{'closing': !loading && initMap, 'closed': hideLoading && initMap}" ref="loadingMessage">
      <div class="loader-background-image"></div>
      <div class="tech-city-loading-circle" v-if="!initMap && allowRenderMap">
        <svg-loading viewBox="0 0 398 398"></svg-loading>
        <div class="tech-city-loading-percentage">
          {{loaded}}
        </div>
      </div>
    </div>
    <div class="last-updated-date" v-if="!techCityModalIsShown && !isShowingSubscriptionPopup">
      Last updated: {{showUpdatedDate}}
    </div>
    <div class="no-js listing" hidden v-if="!techCityModalIsShown">
      <ul>
        <li v-for="(item, index) in property" :key="index" v-if="item.status === 'normal' && item.name">
          <a :href="'/china-tech-city/' + item.data_type + '/' + item.data_key">
            {{item.name}} <template v-if="item.field_also_known_as">({{item.field_also_known_as}})</template></a>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
  import get from 'lodash/get'
  import find from 'lodash/find'
  import round from 'lodash/round'
  import isEmpty from 'lodash/isEmpty'
  import startsWith from 'lodash/startsWith'

  import {
    mapState,
    mapActions,
    mapGetters,
    mapMutations,
  } from 'vuex'

  import { formatShareCount, isDesktop, seoTitleFormatter } from 'mixins/utilities'

  import artboardData from 'static/tech-city/metadata/artBoard'
  import normalData from 'static/tech-city/metadata/normal'
  import buildingData from 'static/tech-city/metadata/building'
  import animationLineData from 'static/tech-city/metadata/animationLine'
  import hoverData from 'static/tech-city/metadata/hover'
  import splitData from 'static/tech-city/metadata/split'
  import pinkDotData from 'static/tech-city/metadata/pinkDot'
  import resourceData from 'static/tech-city/metadata/resource'
  import interactiveData from 'static/tech-city/metadata/interactive'
  import otherData from 'static/tech-city/metadata/other'
  import buildingPolygonData from 'static/tech-city/metadata/buildingPolygon'
  import EventBus from 'components/helpers/event-bus'

  import animationHandler from 'components/mixins/animationHandler'
  import mounted from 'components/mixins/mounted'
  import techCityShareHandler from 'components/mixins/techCityShareHandler'
  import GTMManager from 'mixins/gtm-manager'

  import SvgShareIcon from 'static/svg/icon_share.svg'
  import SvgHiddenShareIcon from 'static/svg/icon_hidden_share.svg'
  import SvgLoading from 'static/svg/raw/loading_circle.svg'
  import SvgEmailCollect from 'static/svg/tc_email_collect.svg'
  import SvgNewsView from 'static/svg/tc_news_view.svg'

  export default {
    name: 'tech-city-map',
    props: {
      isMapView: {
        type: Boolean,
        required: false,
        default: false,
      },
      pageType: {
        type: String,
        required: true,
      },
      showNewsBiteIndicator: {
        type: Boolean,
        default: false,
      },
    },
    mixins: [
      animationHandler,
      mounted,
      GTMManager,
      techCityShareHandler,
    ],
    components: {
      SvgShareIcon,
      SvgHiddenShareIcon,
      SvgLoading,
      SvgEmailCollect,
      SvgNewsView,
      TechCityWelcome: () => import('components/tech-city/map/TechCityWelcome.vue'),
      TechCityModal: () => import('components/tech-city/overlay/TechCityModal.vue'),
      TechCityFallingMoney: () => import('components/tech-city/map/TechCityFallingMoney.vue'),
      TechCitySubscribe: () => import('components/tech-city/map/TechCitySubscribe.vue'),
      PixijsWidget: () => import('components/widgets/PIXIJSWidget.vue'),
      ToggleShareButtons: () => import('components/widgets/ToggleShareButtons.vue'),
    },
    data() {
      return {
        // Pixi will start loading progress since 32%
        // to prevent stucking at 0% for too long
        initialLoadingPercentage: 32,
        artboardData,
        normalData,
        lastClickKey: false,
        isShowPageShareIcon: false,
        isShowPageShareButton: false,
        isClosingModal: false,
        snapActionKey: 'techCityBuildingClick',
        clickedItem: {},
        loading: true,
        showWelcomeMessage: false,
        hideLoading: false,
        snapZoomEnded: false,
        isUpdateMessage: false,
        isShowingSubscriptionPopup: false,
        fallingMoneyActive: false,
        interactiveSpriteConfig: [
          // {
          //   animationKey: 'unicorn_interactive',
          //   dataKey: 'unicorn',
          //   dataPath: '/static/tech-city/interactive/unicorn.json',
          // },
        ],
        animationSpriteConfig: [
          {
            animationKey: 'animation_indicator_grey',
            dataKey: 'pinkDot_grey',
            dataPath: '/static/tech-city/animation/animation_indicator_grey.json',
          },
          {
            animationKey: 'animation_indicator_red',
            dataKey: 'pinkDot_red',
            dataPath: '/static/tech-city/animation/animation_indicator_red.json',
          },
          {
            animationKey: 'animation_indicator_red_news_bites',
            dataKey: 'pinkDot_red_news_bites',
            dataPath: '/static/tech-city/animation/animation_indicator_red_news_bites.json',
          },
        ],
        imageSpriteSheetConfig: {
          desktop: [
            {
              multi: {
                startIndex: 0,
                count: 24,
                indexKey: 'images-',
                dataPath: '/static/tech-city/images/spritesheet',
              },
            },
          ],
          mobile: [
            {
              multi: {
                startIndex: 0,
                count: 24,
                indexKey: 'images-',
                dataPath: '/static/tech-city/images/spritesheet',
              },
            },
          ],

        },
        loadingPercent: 0,
        interval: null,
        shortenUrl: '',
        isShowSubscription: false,
        subscriptionStatusKey: 'comingSoon',
        subscriptionStatusKeyUpdates: 'comingSoonUpdates',
        pandaAnimate: false,
        pandaChatBubble: false,
        pandaLeave: false,
        pandaLeaveNow: false,
        sharePandaIsAwake: false,
        isCityReadyed: false,
        clickedUnicorn: false,
        clickedUnicornTimeout: false,
        initMap: false,
        startLoadingDate: false,
      }
    },
    created() {
      // Don't wanna show the header in loading screen
      this.toggleHeaderTitle(false)
      this.setTechCityImageData({ data: buildingData })
    },
    mounted() {
      if (!this.allowRenderMap && this.isMapView) this.handleDatakey()
      if (get(this, '$refs[techCity]')) this.$refs.techCity.addEventListener('wheel', this.handleWheel)
      this.doProgress()

      this.$root.$on('bv::modal::show', this.handleShowBurgerMenu)
      this.$root.$on('bv::modal::hidden', this.handleHideBurgerMenu)
    },
    computed: {
      ...mapState(['property', 'freezeMap', 'blurMap', 'techCityModalIsShown', 'techCityNewsViewIsShown', 'settings']),
      pixijsWidget() {
        return this.initMap ? this.$refs.pixijsWidget : false
      },
      allowRenderMap() {
        return isEmpty(this.currentCityItem) && this.isMapView
      },
      currentBuildingNid() {
        return get(this, 'currentCityItem.nid')
      },
      currentCityItem() {
        if (!isEmpty(this.clickedItem)) {
          return this.clickedItem
        }
        return find(this.property, {path: this.$route.params.data_key}) || {}
      },
      currentCityItemPath() {
        return get(this, 'currentCityItem.path', '')
      },
      currentCityItemType() {
        return get(this, 'currentCityItem.data_type', '')
      },
      currentCityItemStatus() {
        return get(this, 'currentCityItem.status', '')
      },
      shareUrl() {
        const route = get(this, '$route.fullPath', '')
        if (route && (!this.isShowSubscription && this.techCityModalIsShown)) {
          return `${this.requestDomain()}${route}`
        }
        return `${this.requestDomain()}/china-tech-city`
      },
      extraData() {
        return {
          animationLineData,
          hoverData,
          buildingData,
          splitData: {
            desktop: splitData,
            mobile: splitData,
          },
          pinkDotData,
          resourceData,
          interactiveData,
          otherData,
          buildingPolygonData,
        }
      },
      loaded() {
        return `${round(this.loadingPercent)}%`
      },
      seoTitle() {
        const node = this.$store.getters.node(get(this, 'currentCityItem.nid'))
        return seoTitleFormatter({pageType: this.pageType, node})
      },
      showUpdatedDate() {
        const updatedDate = find(this.property, {key: 'tech_city_last_updated'}) || {}
        return get(updatedDate, 'data', '')
      },
      isShowTopRightButton() {
        return this.techCityModalIsShown || this.techCityNewsViewIsShown
      },
      newsViewEnabled() {
        return get(this, 'settings.NEWS_VIEW', false)
      },
    },
    destroyed() {
      if (get(this, '$refs[techCity]')) this.$refs.techCity.removeEventListener('wheel', this.handleWheel)
      document.getElementsByTagName('html').classList.remove('techcity--root')

      this.$root.$off('bv::modal::show', this.handleShowBurgerMenu)
      this.$root.$off('bv::modal::hidden', this.handleHideBurgerMenu)
    },
    methods: {
      ...mapActions(['fetchSharecount', 'setFreezeMap', 'setBlurMap', 'toggleHeaderTitle']),
      ...mapGetters(['requestDomain']),
      ...mapMutations(['setTechCityImageData', 'setTechCityModalIsShown']),
      isEmpty,
      formatShareCount,
      snapEnd({actionKey}) {
        switch (actionKey) {
        case this.snapActionKey:
          this.pixijsWidget.zoomToSize({actionKey: this.snapActionKey})
          break
        default:
          // DO NTH
        }
      },
      snapZoomEnd({actionKey}) {
        switch (actionKey) {
        case this.snapActionKey:
          this.showModal(true)
          break
        case 'initSnapZoom':
          this.showWelcomeMessage = true
          break
        default:
          // DO NTH
        }
      },
      spriteMouseDown({dataKey}) {
        if (dataKey) {
          this.lastClickKey = dataKey
        }
      },
      spriteMouseClick({
        dataKey, destinateX = false, destinateY = false, isSame = false,
      }) {
        if (this.keyPressing) return
        this.clickedItem = find(this.property, ['data_key', dataKey]) || {}
        const { data_type: dataType } = this.clickedItem

        switch (dataKey) {
        case 'unicorn':
          GTMManager.pushTracker({
            category: 'Inbound Clicks',
            action: 'China Tech City Map View/Feature/Click',
            label: '/unicorn',
          })
          // show falling money
          // TODO: code enhance
          if (!this.clickedUnicorn) {
            if (this.clickedUnicornTimeout) clearTimeout(this.clickedUnicornTimeout)
            this.clickedUnicorn = true
            get(this, '$refs[\'falling-money\'].makeItRain', () => {})()
            this.clickedUnicornTimeout = setTimeout(() => {
              this.clickedUnicorn = false
            }, 5000)
          }
          this.lastClickKey = false
          break
        case 'news-bites':
          this.toggleNewsBitesClick()
          GTMManager.pushTracker({
            category: 'Inbound Clicks',
            action: 'China Tech City Map View/Feature/Click',
            label: `/${dataKey}`,
          })
          break
        default:
          if (!isDesktop()) {
            this.showModal(true)
            GTMManager.pushTracker({
              category: 'Inbound Clicks',
              action: 'China Tech City Map View/Feature/Click',
              label: `/${dataType}/${dataKey}`,
            })
          } else if (dataKey && dataKey === this.lastClickKey) {
            if (!isEmpty(this.clickedItem)) {
              GTMManager.pushTracker({
                category: 'Inbound Clicks',
                action: 'China Tech City Map View/Feature/Click',
                label: `/${dataType}/${dataKey}`,
              })
              const actionKey = dataKey === 'unicorn ' ? 'unicorn' : this.snapActionKey
              if (isSame) {
                this.pixijsWidget.zoomToSize({actionKey})
              } else {
                this.pixijsWidget.mapToXY({actionKey, x: destinateX, y: destinateY })
              }
            }
            this.lastClickKey = false
          }
        }

        this.lastClickKey = false
      },
      mapDragStart() {
        this.lastClickKey = false
      },
      showModal(changeRoute, {status = false} = {}) {
        // show system pop up of news bites
        EventBus.$emit('show-system-popup')

        this.setFreezeMap(true)
        this.setBlurMap(true)

        switch (status || this.currentCityItemStatus) {
        case this.subscriptionStatusKey:
          this.isUpdateMessage = false
          this.showSubscription()
          if (!changeRoute) return
          this.$router.push({
            name: 'TechCityNotify',
            params: {
              data_type: this.currentCityItemType,
              data_key: this.currentCityItemPath,
            },
          })
          break
        case this.subscriptionStatusKeyUpdates:
          this.isUpdateMessage = true
          this.showSubscription()
          if (!changeRoute) return
          this.$router.push({
            name: 'TechCityNotify',
            params: {
              data_type: this.currentCityItemType,
              data_key: this.currentCityItemPath,
            },
          })
          break
        default:
          if (!this.currentCityItemPath) return
          this.toggleBuildingAnimation(false)
          this.setTechCityModalIsShown(true)
          if (!changeRoute) return
          this.$router.push({
            name: 'TechCityPopup',
            params: {
              data_type: this.currentCityItemType,
              data_key: this.currentCityItemPath,
            },
          })
        }
      },
      handleDatakey() {
        const {name: routerName, params: {data_key: dataKey, data_type: dataType} } = this.$route
        if (get(this, 'pixijsWidget')) this.pixijsWidget.updateBlur()
        if (dataKey && dataType) {
          const entry = find(this.property, {
            path: dataKey,
            data_type: dataType,
          })
          if (!isEmpty(entry)) {
            this.clickedItem = find(this.property, ['data_key', dataKey])
            this.showModal(false)
            return true
          }
        }
        return false
      },
      startRenderPixi() {
        // check point for calucate map loading time
        this.startLoadingDate = new Date()
      },
      pixijsIsReady() {
        GTMManager.pushTracker({
          category: 'Tech City/Loading',
          action: 'China Tech City Map View/Loading Time',
          label: new Date() - this.startLoadingDate,
        })
        const {name: routerName, params: {data_key: dataKey, data_type: dataType} } = this.$route
        this.initMap = true
        switch (routerName) {
        case 'TechCityPopup':
          if (!this.handleDatakey()) this.$router.push({name: 'TechCity'})
          break
        case 'TechCityNotify':
          if (!this.handleDatakey()) {
            this.$router.push({name: 'TechCityNotify'})
            this.showModal(false, {status: this.subscriptionStatusKey})
          }
          break
        default:
          if (this.isMapView && !get(this, '$route.query.utm_source')) this.$router.push({name: 'TechCity'})
        }
        this.loading = false

        const loadingAnimationTarget = get(this, '$refs.loadingMessage')
        const loadingAnimationEndHandler = () => {
          this.removeAnimationEndListener(loadingAnimationTarget, [loadingAnimationEndHandler])
          this.hideLoading = true
        }
        this.updateShortenUrl()
        this.bindAnimationEnd(loadingAnimationTarget, [loadingAnimationEndHandler])

        const sharePanda = get(this, '$refs.sharePanda')
        this.bindAnimationEnd(sharePanda, [this.sharePandaAnimationEndHandler])
      },
      doProgress() {
        this.interval = setInterval(() => {
          this.loadingPercent += 1
        }, 200)
      },
      welcomeMessageDone() {
        if (!this.techCityModalIsShown) this.handleCityFirstReady()
      },
      handleCityFirstReady() {
        if (!this.isCityReadyed) {
          this.setFreezeMap(false)
          this.setBlurMap(false)
          this.wakeSharePanda()
          this.isCityReadyed = true
        }
      },
      wakeSharePanda() {
        if (!this.techCityModalIsShown && !this.sharePandaIsAwake) {
          this.sharePandaIsAwake = true
          this.pandaAnimate = true
        }
      },
      closeModal(isClose = false) {
        if (!isClose) {
          this.isClosingModal = true
          setTimeout(() => {
            this.isShowSubscription = false
            this.setTechCityModalIsShown(false)
            if (!startsWith(get(this.$route, 'name', false), 'TechCityNews')) this.$router.push({name: 'TechCity'})
            this.isClosingModal = false
            this.setFreezeMap(false)
            this.setBlurMap(false)
            this.toggleBuildingAnimation(true)
            this.clickedItem = {}
            this.handleCityFirstReady()
          }, 500)
        }
      },
      toggleBuildingAnimation(isShow) {
        const { data_key: dataKey } = this.clickedItem || {}
        if (this.pixijsWidget) {
          if (isShow) {
            get(this, 'pixijsWidget.startResourceAnimation', () => {})({dataKey, delay: 0, init: true})
          } else {
            get(this, 'pixijsWidget.stopResourceAnimation', () => {})({dataKey})
          }
        }
      },
      shareHover(isShow) {
        if (isShow && isDesktop()) {
          GTMManager.pushTracker({
            category: 'Hover',
            action: 'China Tech City Map View/Share/Hover',
          })
        }
      },
      handleWheel(e) {
        if (!this.techCityModalIsShown) e.preventDefault()
      },
      handleShareButton() {
        this.isShowPageShareButton = true
      },
      handleShowBurgerMenu(event, modalId) {
        if (modalId === 'tech-lift-modal') {
          this.setFreezeMap(true)
          this.setBlurMap(true)
        }
      },
      handleHideBurgerMenu(event, modalId) {
        if (modalId === 'tech-lift-modal') {
          this.setFreezeMap(false)
          this.setBlurMap(false)
        }
      },
      shareTechCity() {
        if (get(this, '$refs[share-wrapper]')) {
          if (this.$refs['share-wrapper'].isNativeWrapper || this.$refs['share-wrapper'].isNative) {
            this.$refs['share-wrapper'].nativeShareClick()
          } else {
            this.isShowPageShareIcon = !this.isShowPageShareIcon
          }
        }
      },
      updateShortenUrl() {
        const {name} = this.$route
        if (name === 'TechCity') this.shortenUrl = 'https://abcu.us/city'
      },
      updateShareCount() {
        if (get(this, '$refs[shareButtons]')) this.$refs.shareButtons.fetchShareCount()
      },
      desktopOrMobile() {
        if (isDesktop()) return 'Desktop'
        return 'Mobile'
      },
      sharePandaAnimationEndHandler() {
        this.pandaChatBubble = true
        this.pandaLeave = true
      },
      showSubscription() {
        if (!this.isShowSubscription) {
          this.isShowSubscription = true
          if (get(this, '$refs[subscription]')) this.$refs.subscription.togglePopup()
          GTMManager.pushTracker({
            category: 'Tech City',
            action: 'Tech City/Get Update/Click',
            label: this.isUpdateMessage ? '/china-tech-city/' : `/china-tech-city/${this.currentCityItemType}/${this.currentCityItemPath}`,
          })
        }
      },
      subscriptionPopupHandler(value) {
        if (!value) {
          this.closeModal()
          this.isShowingSubscriptionPopup = false
        } else {
          this.isShowingSubscriptionPopup = true
        }
      },
      onPixiLoaderOnStart() {
        this.loadingPercent = this.initialLoadingPercentage
      },
      onPixiLoaderOnProgress({ progress }) {
        this.loadingPercent = this.initialLoadingPercentage + (progress / 100 * (100 - this.initialLoadingPercentage))
      },
      onPixiLoaderOnComplete({ progress }) {
        this.loadingPercent = this.initialLoadingPercentage + (progress / 100 * (100 - this.initialLoadingPercentage))
      },
      toggleNewsBitesClick() {
        this.$emit('newsViewButtonOnClick', true)
      },
    },
    watch: {
      $route(after) {
        const {data_key: dataKey} = this.$route.params
        this.clickedItem = find(this.property, ['data_key', dataKey])
        if (get(after, 'params.data_key') && get(after, 'params.data_type') && !this.techCityModalIsShown) this.showModal(true)
        if ((!get(after, 'params.data_key') || !get(after, 'params.data_type')) && this.techCityModalIsShown) this.closeModal()

        this.updateShortenUrl()
        this.updateShareCount()
      },
      loadingPercent(val) {
        if (val >= this.initialLoadingPercentage) {
          clearInterval(this.interval)
        }
      },
    },
  }
</script>
<style lang="scss" scoped>
  $boxShadow: 0 0 14px 0 rgba(0, 0, 0, 0.5);

  @mixin circle-button {
    fill: $white;
    padding: rem-calc(14);
    line-height: rem-calc(20);
    border-radius: 50%;
    line-height: 0;
    box-shadow: $boxShadow;
    margin-bottom: rem-calc(15);
    cursor: pointer;
  }

  .tech-city{
    /deep/ {
      .toggle-share-wrapper.with-count {
        @include respond-to('mobile') {
          bottom: rem-calc(10);
          right: rem-calc(10);
          height: rem-calc(58);
        }
      }

      .share-panda {
        position: absolute;
        right: 0;
        bottom: rem-calc(33);
        z-index: 10;
        width: 100%;
        transform-origin: center 79%;
        transform: scale(0);
        @include respond-to('desktop') {
          transform: scale(0);
          bottom: rem-calc(40);
        }
        &.animate {
          animation: pandaShow .2s linear;
          animation-delay: 1.5s;
          transform: scale(0);
          animation-fill-mode: forwards;
          &.leave {
            transform: scale(1);
            animation-delay: 6s !important;
            animation: pandaLeave .2s linear;
            animation-fill-mode: forwards;
          }

          &.no-delay {
            transform: scale(1);
            animation-delay: 0s !important;
            animation: pandaLeaveNoDelay .2s linear;
            animation-fill-mode: forwards;
          }

        }

        .panda {
          text-align: right;
          padding-right: rem-calc(22);
          .panda-icon {
            background: url('/static/svg/raw/icon_panda_with_hat.svg');
            width: rem-calc(74);
            height: rem-calc(59);
            display: inline-block;
          }
        }
        .chat-bubbles {
          background: $white;
          padding: rem-calc(7 20);
          text-align: center;
          color: $black;
          border-radius: rem-calc(26);
          position: absolute;
          white-space: nowrap;
          right: calc(100% - 60px);
          top: rem-calc(10);
          font-size: rem-calc(14);
          font-weight: 500;
          position: absolute;
          display: none;
          &:before {
            content: "";
            width: 0;
            height: 0;
            position: absolute;
            border-left: rem-calc(16) solid $white;
            border-right: rem-calc(10) solid transparent;
            border-top: rem-calc(6) solid $white;
            border-bottom: rem-calc(2) solid transparent;
            right: rem-calc(-10);
            top: rem-calc(24);
          }
          &.slide-in {
            display: initial;
            transform: translateX(rem-calc(100));
            animation: chatBubbleSlideIn .2s linear;
            animation-fill-mode: forwards;
            opacity: 0;
          }
        }
      }
    }
    .tech-city-map {
      z-index: 2;
      position: relative;

      &::before {
        content: " ";
        display: block;
        height: rem-calc(200);
        opacity: 0.5;
        background-image: linear-gradient(to top, rgba(0, 0, 0, 0), rgb(0, 0, 0));
        position: absolute;
        left: rem-calc(-20);
        right: rem-calc(-20);
        z-index: 1;
        user-select: none;
        pointer-events: none;

        @include respond-to("desktop") {
          left: rem-calc(-40);
          right: rem-calc(-40);
        }
      }
    }
  }


  @include respond-to("mobile") {
    .modal-nav {
      .close-button {
        display: none;
      }
    }
  }

  .show-modal .overlay-welcome /deep/ {
    .overlay-bubble {
      background-color: $rose-90;
    }
    .overlay-bubble:after {
      border-top-color: $rose-90;
    }
  }

  .loader-overlay {
    opacity: 1;
    z-index: 4;
    animation-delay: 300ms;
    height: 100vh;
    width: 100vw;
    &.closing {
      animation: loaderLoaded 300ms forwards;
    }
    &.closed {
      display: none;
    }
  }
  .loader {
    opacity: 0;
  }
  .loader-background-image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    opacity: 1;
    transition: opacity 300ms linear;
    @include respond-to("mobile") {
      background-image: url(/static/images/techcity_loading_mobile.jpg);
    }
    @include respond-to("desktop") {
      background-image: url(/static/images/techcity_loading.jpg);
    }
    @include respond-to("desktop_wide") {
      background-image: url(/static/images/techcity_loading_hd.jpg);
    }

    .closing & {
      opacity: 0;
    }
  }
  .tech-city-loading-percentage {
    font-size: rem-calc(80);
    font-weight: 200;
    font-family: $barlow;
    text-align: right;
    color: $white;
    width: rem-calc(150);
    margin: 0 auto;
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    margin-top: rem-calc(-60px);
  }
  .tech-city-loading-circle {
    width: rem-calc(270);
    height: rem-calc(270);
    position: absolute;
    margin: rem-calc(-135) auto 0 rem-calc(-135);
    left: 50%;
    transform: scale(.65);
    top: 50%;

    @include respond-to("mobile") {
      margin: rem-calc(-135) auto 0 rem-calc(-135);
    }

    @include respond-to("hd") {
      transform: none;
    }

    svg {
      will-change: transition;
      max-height: 100%;
      max-width: 100%;
      animation: rotate360 3s infinite linear;
    }
  }
  .tech-city-top-right-button {
    .indicator {
      position: absolute;
      width: rem-calc(8);
      height: rem-calc(8);
      background-color: $red;
      border-radius: rem-calc(10);
      right: rem-calc(-3);
      top: rem-calc(3);
      @include respond-to('desktop'){
        right: rem-calc(16);
        top: rem-calc(10);
      }
    }
    &.email-collect-button{
      right: rem-calc(233);
      @include respond-to('mobile'){
        right: rem-calc(109);
      }
      &.news-view-invisible {
        right: rem-calc(62);
      }
    }
    &.news-view-button{
      right: rem-calc(62);
    }
    color: $white;
    fill: $white;
    font-size: rem-calc(14);
    position: absolute;
    z-index:1999;
    top: rem-calc(16);
    cursor: pointer;
    transition: transform 350ms ease;
    transform: translateY(0);
    will-change: transform;

    &.hidden {
      transform: translateY(-150%);
    }

    span {
      display: none;
    }

    @include respond-to("desktop") {
      height: rem-calc(40);
      border-radius: rem-calc(21.5);
      background: $black-15;
      padding: rem-calc(10 21);
      top: rem-calc(25);
      right: rem-calc(67);
      span {
        display: inline-block;
        margin-right: rem-calc(10);
      }
    }

    svg {
      display: inline-block;
      width:  rem-calc(24);
      height: rem-calc(18);

      @include respond-to("desktop") {
        margin-bottom: rem-calc(-2);
      }
    }
  }
  @keyframes rotate360 {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(720deg);
    }
  }

  @keyframes loaderLoaded {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes pandaShow {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes pandaLeave {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(0);
      display: none;
    }
  }

  @keyframes pandaLeaveNoDelay {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(0);
      display: none;
    }
  }

  @keyframes chatBubbleSlideIn {
    0% {
      transform: translateX(rem-calc(100));
    }
    50% {
      transform: translateX(rem-calc(20));
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .last-updated-date {
    font-size: rem-calc(12);
    color: $white;
    text-shadow: $black-70 rem-calc(0 1 5);
    position: absolute;
    bottom: rem-calc(5);
    left: rem-calc(10);
    z-index: 5;
  }

</style>
<style>
  .techcity-root, .techcity-root body {
    overscroll-behavior: none;
    overflow: hidden;
    height: 100%;
    width: 100%;
  }
  .techcity-root #app[data-browser="ios"] {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
</style>



// WEBPACK FOOTER //
// components/tech-city/TechCityMap.vue